import React, { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Flame, Zap, Award, CheckCircle2, BookOpen, TrendingUp, GraduationCap, Trophy } from "lucide-react";
import { cn } from "../lib/utils";
import { toast } from "sonner";

const ALL_BADGES = [
  { name: "Curious Learner", desc: "Earn 50 points", icon: BookOpen },
  { name: "Market Scholar", desc: "Earn 200 points", icon: GraduationCap },
  { name: "3-Day Streak", desc: "Check in 3 days in a row", icon: Flame },
  { name: "Week Warrior", desc: "Check in 7 days in a row", icon: Award },
  { name: "First Profit", desc: "Portfolio above $100k", icon: TrendingUp },
  { name: "$105K Club", desc: "Portfolio reaches $105k", icon: Trophy },
  { name: "$110K Club", desc: "Portfolio reaches $110k", icon: Trophy },
  { name: "$125K Club", desc: "Portfolio reaches $125k", icon: Trophy },
  { name: "$150K Club", desc: "Portfolio reaches $150k", icon: Trophy },
];

const LESSONS = [
  { t: "What is a score?", d: "Each stock is scored 1–100 by blending momentum (40%), moving-average trend (35%) and RSI position (25%)." },
  { t: "RSI is a guardrail", d: "We penalize overbought (RSI > 70) rather than treating high RSI as a buy signal — it protects against chasing." },
  { t: "Risk ≠ Score", d: "Risk (Low/Med/High) comes from volatility (ATR), separate from the score. A high score can still be high risk." },
  { t: "Beat the index", d: "A past pick is only 'correct' if it beat the S&P 500 next day — an honest, hard-to-game benchmark." },
];

export default function Learn() {
  const { user, refreshUser } = useAuth();
  const [checking, setChecking] = useState(false);
  const earned = new Set(user?.badges || []);

  const today = new Date().toISOString().slice(0, 10);
  const checkedIn = user?.last_checkin === today;

  const checkin = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/gamify/checkin");
      await refreshUser();
      if (data.already) toast("Already checked in today ✔");
      else toast.success(`+10 points! Streak: ${data.streak} day${data.streak > 1 ? "s" : ""} 🔥`);
    } catch {
      toast.error("Check-in failed");
    } finally { setChecking(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight">Learn & Earn</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Points & streaks reward <strong className="text-foreground">learning actions only</strong> — never "winning picks".
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-indigo-500/20 bg-indigo-500/10 p-6 flex flex-col items-center text-center">
          <Flame className={cn("h-9 w-9 text-indigo-400 mb-2", user?.streak > 0 && "ss-flame")} />
          <div className="text-4xl font-black font-mono text-indigo-400">{user?.streak || 0}</div>
          <p className="text-xs text-muted-foreground mt-1">day streak</p>
        </div>
        <div className="rounded-2xl border border-primary/20 bg-primary/10 p-6 flex flex-col items-center text-center">
          <Zap className="h-9 w-9 text-primary mb-2" />
          <div className="text-4xl font-black font-mono text-primary">{user?.points || 0}</div>
          <p className="text-xs text-muted-foreground mt-1">points</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6 flex flex-col items-center justify-center text-center">
          <Button data-testid="checkin-btn" onClick={checkin} disabled={checking || checkedIn}
            className={cn("gap-2 h-11 w-full", checkedIn ? "bg-secondary text-muted-foreground" : "bg-primary text-primary-foreground hover:bg-primary/90")}>
            <CheckCircle2 className="h-4 w-4" /> {checkedIn ? "Checked in today" : "Daily check-in (+10)"}
          </Button>
          <p className="text-xs text-muted-foreground mt-2">Read a "Why this pick?" card for +5</p>
        </div>
      </div>

      <div>
        <h2 className="text-lg font-heading font-semibold mb-3">Badges</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="badges-grid">
          {ALL_BADGES.map((b) => {
            const has = earned.has(b.name);
            return (
              <div key={b.name} className={cn("rounded-2xl border p-5 text-center transition-all",
                has ? "border-primary/30 bg-primary/5" : "border-border bg-card opacity-60")}>
                <b.icon className={cn("h-8 w-8 mx-auto mb-2", has ? "text-primary" : "text-muted-foreground")} />
                <div className="font-semibold text-sm">{b.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{b.desc}</div>
                {has && <div className="text-[10px] font-mono uppercase tracking-widest text-primary mt-2">Earned</div>}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h2 className="text-lg font-heading font-semibold mb-3 flex items-center gap-2"><TrendingUp className="h-5 w-5 text-primary" /> Quick Lessons</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {LESSONS.map((l) => (
            <div key={l.t} className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-semibold mb-1.5">{l.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{l.d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
